/**
 * Phonebook Contact Types
 *
 * Contacts are the PRIMARY source for all business relationships.
 * Contacts can be designated as suppliers, customers, or both.
 *
 * Flow: Create contact in phonebook -> Assign as supplier/customer -> Creates linked record
 */
// === LABELS ===
export const CONTACT_DESIGNATION_LABELS = {
    none: 'Unassigned',
    supplier: 'Supplier',
    customer: 'Customer',
    both: 'Supplier & Customer'
};
export const CONTACT_STATUS_LABELS = {
    active: 'Active',
    inactive: 'Inactive'
};
// === COLOR CODING ===
export const CONTACT_DESIGNATION_COLORS = {
    none: '#6b7280', // Gray - unassigned
    supplier: '#3b82f6', // Blue - supplier
    customer: '#10b981', // Green - customer
    both: '#8b5cf6' // Purple - both
};
export const CONTACT_STATUS_COLORS = {
    active: '#10b981', // Green
    inactive: '#6b7280' // Gray
};
/**
 * Validates contact data
 */
export function validateContact(contact) {
    const errors = [];
    // Required fields
    if (!contact.companyName?.trim()) {
        errors.push({ field: 'companyName', message: 'Company name is required' });
    }
    if (!contact.contactPerson?.trim()) {
        errors.push({ field: 'contactPerson', message: 'Contact person is required' });
    }
    if (!contact.email?.trim()) {
        errors.push({ field: 'email', message: 'Email is required' });
    }
    if (!contact.phone?.trim()) {
        errors.push({ field: 'phone', message: 'Phone is required' });
    }
    // Email format
    if (contact.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(contact.email)) {
        errors.push({ field: 'email', message: 'Invalid email format' });
    }
    return {
        isValid: errors.length === 0,
        errors
    };
}
/**
 * Calculate contact statistics from a list of contacts
 */
export function calculateContactStatistics(contacts) {
    const stats = {
        total: contacts.length,
        byDesignation: {
            none: 0,
            supplier: 0,
            customer: 0,
            both: 0
        },
        byStatus: {
            active: 0,
            inactive: 0
        },
        suppliersCount: 0,
        customersCount: 0,
        unassignedCount: 0,
        linkedUsersCount: 0
    };
    contacts.forEach(contact => {
        // Count by designation
        stats.byDesignation[contact.designation]++;
        // Count by status
        stats.byStatus[contact.status]++;
        // Count suppliers (includes 'supplier' and 'both')
        if (contact.designation === 'supplier' || contact.designation === 'both') {
            stats.suppliersCount++;
        }
        // Count customers (includes 'customer' and 'both')
        if (contact.designation === 'customer' || contact.designation === 'both') {
            stats.customersCount++;
        }
        // Count unassigned
        if (contact.designation === 'none') {
            stats.unassignedCount++;
        }
        // Count linked users
        if (contact.userId) {
            stats.linkedUsersCount++;
        }
    });
    return stats;
}
/**
 * Filter contacts based on filter criteria
 */
export function filterContacts(contacts, filters) {
    return contacts.filter(contact => {
        // Search filter
        if (filters.search) {
            const search = filters.search.toLowerCase();
            const matchesSearch = contact.companyName.toLowerCase().includes(search) ||
                contact.contactPerson.toLowerCase().includes(search) ||
                contact.email.toLowerCase().includes(search) ||
                contact.phone.includes(search) ||
                (contact.address?.city?.toLowerCase().includes(search)) ||
                (contact.tags?.some(tag => tag.toLowerCase().includes(search)));
            if (!matchesSearch)
                return false;
        }
        // Designation filter
        if (filters.designation && filters.designation !== 'all') {
            if (contact.designation !== filters.designation)
                return false;
        }
        // Status filter
        if (filters.status && filters.status !== 'all') {
            if (contact.status !== filters.status)
                return false;
        }
        return true;
    });
}
/**
 * Creates a default contact object
 */
export function createDefaultContact() {
    return {
        type: 'contact',
        companyName: '',
        contactPerson: '',
        email: '',
        phone: '',
        designation: 'none',
        status: 'active',
        tags: []
    };
}
/**
 * Format contact for display (similar to formatSupplierName)
 */
export function formatContactName(contact) {
    return contact.companyName;
}
/**
 * Get designation badge info for UI
 */
export function getDesignationBadge(designation) {
    const labels = {
        none: { label: 'Unassigned', color: '#374151', bgColor: '#f3f4f6' },
        supplier: { label: 'Supplier', color: '#1e40af', bgColor: '#dbeafe' },
        customer: { label: 'Customer', color: '#065f46', bgColor: '#d1fae5' },
        both: { label: 'Both', color: '#5b21b6', bgColor: '#ede9fe' }
    };
    return labels[designation];
}
